import React, { useMemo } from 'react';
import { KiteGrid, KiteGridCell } from '@kite/react-kite';
import { graphql, useStaticQuery } from 'gatsby';
import { CardTrioCard } from '../CardTrio/CardTrioCard';
import sortLeadership from '../../shared/sortLeadership';
import './LeaderCards.scss';

export const LeaderCards = ({ edic }: { edic: boolean }) => {
    const viewData = useStaticQuery(graphql`
        query LeaderCards {
            pagesV1: allNodeLeadership(
                sort: [
                    { field_top_leadership: DESC }
                    { field_featured_weight: ASC }
                    { field_featured: DESC }
                    { field_last_name: ASC }
                ]
            ) {
                nodes {
                    id
                    field_board_of_directors
                    field_edic
                    field_featured
                    field_featured_weight
                    field_top_leadership
                    field_last_name
                    title
                    fields {
                        url
                    }
                    field_ldr_headshot {
                        alt
                    }
                    field_leadership_description {
                        value
                    }
                    field_leadership_position_title
                    field_top_leadership
                    relationships {
                        field_ldr_headshot {
                            localFile {
                                childImageSharp {
                                    ...ImageConstrainedSettings
                                }
                            }
                        }
                    }
                }
            }
            pagesV2: allNodeLeadershipBioV2(
                sort: [
                    { field_top_leadership: DESC }
                    { field_featured_weight: ASC }
                    { field_featured: DESC }
                    { field_last_name: ASC }
                ]
            ) {
                nodes {
                    id
                    field_board_of_directors
                    field_edic
                    field_featured
                    field_featured_weight
                    field_top_leadership
                    field_last_name
                    title
                    fields {
                        url
                    }
                    field_ldr_headshot {
                        alt
                    }
                    field_leadership_position_title
                    field_top_leadership
                    relationships {
                        field_ldr_headshot {
                            localFile {
                                childImageSharp {
                                    ...ImageConstrainedSettings
                                }
                            }
                        }
                    }
                }
            }
        }
    `);

    const topLeaders = [];
    const leaders = [];

    // Combine the V1 and V2 Leadership Bio nodes into one array
    const allLeaders = [...viewData.pagesV1.nodes, ...viewData.pagesV2.nodes];

    // Sort the Leadership Bio Nodes first by top leadership, then by featured weight, and lastly by last name
    const sortedLeaders = sortLeadership(allLeaders);

    sortedLeaders
        ?.filter((leader) => leader.field_edic === edic) //Return leaders that match "field_edic" field with the passed "edic" prop (both boolean fields)...
        .filter((leader) => edic || leader.field_featured) //..and then filter "field_edic" leaders again that have "edic" OR (queried) "field_featured" as TRUE
        .map((leader) => {
            if (leader.field_top_leadership) {
                topLeaders.push(leader);
            } else {
                leaders.push(leader);
            }
        });

    const gridClasses = useMemo(() => {
        let classes = ``;
        if (leaders.length % 4 >= 1 || leaders.length % 4 === 0) {
            classes = classes + `four-col `;
        }
        if (leaders.length % 3 >= 1 || leaders.length % 3 === 0) {
            classes += `three-col `;
        }
        return classes;
    }, [leaders]);

    const TopLeadershipRow = () => (
        <>
            {!!topLeaders.length && (
                <div className={`top-leadership ${gridClasses}`}>
                    {topLeaders.map((topLeader) => (
                        <KiteGridCell
                            key={topLeader.id}
                            col={6}
                            className="emphasize"
                        >
                            <CardTrioCard
                                card={{
                                    horizontal: true,
                                    id: topLeader.id,
                                    title: topLeader.title,
                                    image: topLeader.relationships
                                        .field_ldr_headshot?.localFile,
                                    imageAlt: `${topLeader.title}'s headshot`,
                                    news: true,
                                    description:
                                        topLeader.field_leadership_position_title,
                                    linkUrl: topLeader.fields.url,
                                    linkText: `view the full bio for ${topLeader.title}`,
                                }}
                            />
                        </KiteGridCell>
                    ))}
                </div>
            )}
        </>
    );

    const LeadershipGrid = () => {
        return (
            <>
                {leaders.map((leader) => {
                    return (
                        <KiteGridCell
                            key={leader.id}
                            col={3}
                            className={`${gridClasses}`}
                        >
                            <CardTrioCard
                                card={{
                                    horizontal: false,
                                    id: leader.id,
                                    title: leader.title,
                                    image: leader.relationships
                                        .field_ldr_headshot?.localFile,
                                    imageAlt: `${leader.title}'s headshot`,
                                    news: true,
                                    description:
                                        leader.field_leadership_position_title,
                                    linkUrl: leader.fields.url,
                                    linkText: `view the full bio for ${leader.title}`,
                                }}
                            />
                        </KiteGridCell>
                    );
                })}
            </>
        );
    };

    return (
        <section className="kite-container leadership__cards">
            <KiteGrid className="top-leader-grid">
                <TopLeadershipRow />
            </KiteGrid>
            <KiteGrid className={`leader-grid ${gridClasses}`}>
                <LeadershipGrid />
            </KiteGrid>
        </section>
    );
};
